import { useEffect } from 'react';
import { navigateFromNotification, type NotificationTapData } from '../navigation/notificationNav';

function tapDataFromRecord(data: Record<string, string> | undefined): NotificationTapData {
  const d = data || {};
  return {
    type: d.type,
    chatId: d.chatId,
    chatKind: d.chatKind,
    messageId: d.messageId,
    senderId: d.senderId,
    senderName: d.senderName,
    planId: d.planId,
    requestId: d.requestId,
    requesterId: d.requesterId,
    requesterName: d.requesterName,
    otherUserId: d.otherUserId,
    isStudiZone: d.isStudiZone,
  };
}

/**
 * Web counterpart to useNotificationListeners.ts. Covers the two ways a
 * notification tap reaches the page on web:
 *
 *  - Background tap with a tab already open: the service worker's
 *    `notificationclick` handler (src/web/firebaseMessagingSw.ts) focuses
 *    that tab and postMessages the notification's data to it — handled here.
 *  - Cold start / no tab open: the SW instead opens a fresh tab at
 *    `/?tonyteNotification=<query-string>`; parsed here on mount. Both paths
 *    call the exact same navigateFromNotification() router notificationNav.ts
 *    already uses for native, which itself queues until nav/auth are ready —
 *    refresh-safe by construction, no extra gating needed here.
 */
export function useNotificationListeners(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const params = new URLSearchParams(window.location.search);
    const encoded = params.get('tonyteNotification');
    if (encoded) {
      const data: Record<string, string> = {};
      new URLSearchParams(encoded).forEach((value, key) => {
        data[key] = value;
      });
      navigateFromNotification(tapDataFromRecord(data));
      // Strip the param so a later manual refresh doesn't re-navigate.
      params.delete('tonyteNotification');
      const nextSearch = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (nextSearch ? `?${nextSearch}` : ''));
    }

    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'TONYTE_NOTIFICATION_CLICK') return;
      navigateFromNotification(tapDataFromRecord(event.data.data));
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);
}
